// istanbul ignore file
import React, { useState, useEffect } from 'react'
import { Footer, Header, SidebarLayout } from '@scuf/common'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import IdleModal from './IdleModal'
import { getLicenseFileUrl, getUserGuideFileUrl } from '../../../services/fileDownloadService'
import { escapeHTML } from '../../../utils/escapeHTML'
import { changeHamburgerState } from '../redux/hamburgerSlice'
import LanguageStrings from '../../../i18n/locales'
import CollapsibleSidebarItem from './CollapsibleSidebarItem'
import HomeIcon from '../../../assets/hamburg/HomeIcon.png'
import HomeIconDisabled from '../../../assets/hamburg/HomeIconDisabled.png'
import TenantIcon from '../../../assets/hamburg/Tenant_Name.svg'
import SettingIcon from '../../../assets/hamburg/SettingIcon.png'
import KpiIcon from '../../../assets/hamburg/KpiIcon.png'
import HelpIcon from '../../../assets/hamburg/Help.svg'
// import EnterpriseIcon from '../../../assets/hamburg/EnterpriseIcon.png'
import SiteIcon from '../../../assets/hamburg/Site_Config.svg'
import GetStarted from '../../../assets/hamburg/Get_started.svg'
import Bookmarks from '../../../assets/hamburg/Bookmark.svg'
import Bookmark_disabled from '../../../assets/hamburg/Bookmark_disabled.png'
import AssetIcon from '../../../assets/hamburg/AssetIcon.png'
import DeviceIcon from '../../../assets/hamburg/DeviceIcon.png'
import { connect, type ConnectedProps, useDispatch } from 'react-redux'
import { fetchSidebarMenuInfo } from './../redux/actionCreator'
import { type MenuInfoState } from './../redux/menuInfoSlice'
import { type RootState } from './../../../store'
import { Tooltip, CircularProgress } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import { APIConfiguration } from './../../../utils/apiConfiguration'
import CustomToast from './../../site-management/components/ToastComponent'
import { getBookmarkListDataservice } from '../../bookmarks-home/redux/actionCreator'
import { useSelector } from 'react-redux'
import './CardDashboard.css'

const dashboardStrings = LanguageStrings().Dashboard
const httpStatus = APIConfiguration

interface Device {
  id: string
  name: string
  deviceTypeId: string
}
interface Gateway {
  device: Device[]
}
interface Asset {
  id: string
  name: string
  assetType: string
  gateway?: Gateway[]
}

const DashboardLayout = (props: AllProps): any => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const auth = useAuth()
  const location = useLocation()

  // istanbul ignore next
  const serviceVersion =
  window._env_?.REACT_APP_SERVICE_VERSION !== undefined
    ? window._env_.REACT_APP_SERVICE_VERSION
    : ''

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeItem, setActiveItem] = useState('home')
  const [activeItemId, setActiveItemId] = useState('')
  const [counter, setCounter] = React.useState(0)
  const [hierarchyPaths, setHierarchyPaths] = useState<string[]>([])
  const [licenseFileUrl, setLicenseFileUrl] = useState<string>('')
  const [userGuideUrl, setUserGuideUrl] = useState<string>('')
  const getBookmarkData = useSelector(
    (state: RootState) => state.GetBookmarkData.getBookmarkData
  )
  const getBookmarkDataRaw = useSelector(
    (state: RootState) => state.GetBookmarkData
  )
  const defaultHomeScreen = getBookmarkData?.filter(
    (e) => e?.bookmarkType === 'HOMESCREEN'
  )?.[0]

  useEffect(() => {
    // Refreshing page
  }, [getBookmarkDataRaw])

  useEffect(() => {
    const homeScreens = getBookmarkData?.filter(
      (e) => e?.bookmarkType === 'HOMESCREEN'
    ) ?? []
    if (
      !props.menuInfo.isLoading &&
      !getBookmarkDataRaw.isLoading &&
      location?.pathname === '/'
    ) {
      // If a new user logs in for the first time without any bookmarks or site creation
      if (
        (props.menuInfo?.menuInfo?.length === undefined ||
          props.menuInfo?.menuInfo?.length === 0) &&
        (getBookmarkData?.length === 0 || getBookmarkData?.length === undefined)
      ) {
        handleGetStartedOnClick()
      } else if (homeScreens?.length === 0) {
        const firstSite = props.menuInfo?.menuInfo?.[0]?.site
        const firstAsset = firstSite?.assetList?.[0]
        const firstGateway = firstAsset?.gateway?.[0]
        const firstDevice = firstGateway?.device?.[0]
        if (firstDevice !== undefined) {
          setActiveItem('dashboard')
          setActiveItemId(firstDevice?.id)
          setHierarchyPaths([firstSite?.id, firstAsset?.id, firstDevice?.id])
          handleDeviceClick(
            firstSite?.id,
            firstAsset?.id,
            firstDevice?.deviceTypeId,
            firstDevice?.id,
            firstSite?.name,
            firstAsset?.name,
            firstDevice?.name
          )
        } else if (firstAsset !== undefined) {
          setActiveItem('dashboard')
          setActiveItemId(firstAsset?.id)
          setHierarchyPaths([firstSite?.id, firstAsset?.id])
          handleCollapsibleAssetClick(
            firstSite?.id,
            firstAsset?.id,
            firstSite?.name,
            firstAsset?.name,
            firstDevice?.deviceTypeId
          )
        }
      } else if (homeScreens !== undefined && homeScreens?.length > 0) {
        // If the user has a home screen bookmark, set it as the active item
        const firstHomeScreen = homeScreens?.[0]
        setActiveItem('dashboard')
        setActiveItemId(firstHomeScreen?.deviceID ?? firstHomeScreen?.assetID ?? '')
        setHierarchyPaths([
          firstHomeScreen?.siteID ?? '',
          firstHomeScreen?.assetID ?? '',
          firstHomeScreen?.deviceID ?? ''
        ])
        handleHomeOnClick(firstHomeScreen)
      }
    } else if (location?.pathname === '/device-dashboard' || location?.pathname === '/asset-dashboard') {
      const navigateState = location.state
      const parentDeviceId = navigateState != null ? navigateState.deviceId : ''
      const parentSiteId =
        navigateState != null ? navigateState.parentSiteId : ''
      const parentAssetId =
        navigateState != null ? navigateState.parentAssetId : ''
      setActiveItem('dashboard')
      setActiveItemId(parentDeviceId ?? parentAssetId)
      setHierarchyPaths([parentSiteId, parentAssetId, parentDeviceId])
    }
  }, [props.menuInfo.isLoading, getBookmarkDataRaw])

  useEffect(() => {
    if (auth.isAuthenticated) {
      props?.fetchSidebarMenuInfo() // Trigger API call only after login
      props?.getBookmarkListDataService(auth.user?.profile.sub ?? '')
    }

    // Cleanup function to revoke blob URLs
    return () => {
      if (licenseFileUrl !== '') {
        window.URL.revokeObjectURL(licenseFileUrl)
      }
      if (userGuideUrl !== '') {
        window.URL.revokeObjectURL(userGuideUrl)
      }
    }
  }, [auth.isAuthenticated])

  React.useEffect(() => {
    if (
      !props.menuInfo?.isLoading &&
      props.menuInfo?.httpStatus !== httpStatus.SUCCESS_HTTP_STATUS
    ) {
      if (counter < 10) {
        setCounter((counter) => counter + 1)
        props?.fetchSidebarMenuInfo()
      } else {
        if (
          props.menuInfo?.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS
        ) {
          CustomToast({
            type: 'error',
            heading: 'Access Forbidden',
            message: 'You do not have permission to access this resource.'
          })
        } else if (props.menuInfo?.httpStatus === httpStatus.BAD_REQUEST) {
          CustomToast({
            type: 'error',
            heading: 'Bad Request',
            message: 'The request was invalid. Please try again.'
          })
        } else if (
          props.menuInfo?.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS
        ) {
          CustomToast({
            type: 'error',
            heading: 'Unauthorized',
            message: 'You are not authorized, Please contact administrator.'
          })
        } else if (
          props.menuInfo?.httpStatus === httpStatus.INTERNAL_SERVER_ERROR
        ) {
          CustomToast({
            type: 'error',
            heading: 'Internal Server Error',
            message:
              'An internal server error occurred. Please try again later.'
          })
        } else if (
          props.menuInfo?.httpStatus === httpStatus.NO_DATA_FOUND_HTTP_STATUS
        ) {
          CustomToast({
            type: 'info',
            heading: 'NO DATA FOUND',
            message: 'No System configuration found. Please contact administrator.'
          })
        } else {
          CustomToast({
            type: 'error',
            heading: 'Error',
            message: 'An unexpected error occurred. Please try again later.'
          })
        }
      }
    }
  }, [props?.menuInfo])

  useEffect(() => {
    const path = location.pathname
    const routes = {
      home: ['/home'],
      site: [
        '/site-list',
        '/create-site',
        '/edit-site/:id',
        '/thermal-asset-list/:id',
        '/create-asset/:id',
        '/edit-asset/:id',
        '/gateway-list/:id',
        '/create-gateway/:id',
        '/edit-gateway/:id',
        '/device-list/:id',
        '/create-device/:id',
        '/edit-device/:id'
      ],
      kpi: ['/custom-kpi-list', '/create-custom-kpi', '/edit-custom-kpi'],
      services: ['/asset-dashboard'],
      dashboard: ['/device-dashboard'],
      collapseRoutes: [
        '/site-list',
        '/custom-kpi-list',
        '/custom-device-template-list'
      ],
      started: ['/get-started'],
      settings: ['/thermal-dashboard']
    }
    if (routes.site.some((route) => pathMatch(route, path))) {
      setActiveItem('site')
    } else if (routes.kpi.includes(path)) setActiveItem('kpi')
    else if (routes.dashboard.includes(path)) setActiveItem('dashboard')
    else if (routes.started.includes(path)) setActiveItem('getStarted')
    else if (routes.settings.includes(path)) setActiveItem('services')
    else setActiveItem('home')
    if (routes.collapseRoutes.includes(path)) {
      if (!sidebarCollapsed) onCollapsedClick()
    }
    // Active Menu when nagivate from any other location
    if (location?.state?.parentDeviceId !== undefined) {
      setActiveItemId(location?.state?.parentDeviceId)
    } else if (location?.state?.parentAssetId !== undefined) {
      setActiveItemId(location?.state?.parentAssetId)
    }
  }, [location.pathname])

  // Function to match dynamic routes like /edit/:id
  const pathMatch = (route: string, path: string): boolean => {
    const routeRegex = new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`)
    return routeRegex.test(path)
  }

  const onCollapsedClick = (): any => {
    dispatch(changeHamburgerState(sidebarCollapsed ? 'open' : 'close'))
    setSidebarCollapsed(!sidebarCollapsed)
  }
  const onLogout = (): void => {
    auth
      .signoutRedirect({
        post_logout_redirect_uri: escapeHTML(window.location.origin) + '/logout'
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const handleHomeOnClick = (bookmark: any): void => {
    setHierarchyPaths([bookmark?.siteID, bookmark?.assetID, bookmark?.deviceID ?? ''])
    setActiveItem('dashboard')
    setActiveItemId(bookmark?.deviceID ?? bookmark?.assetID)
    navigate(bookmark?.deviceID !== null && bookmark?.deviceID !== undefined ? '/device-dashboard' : 'asset-dashboard', {
      state: {
        parentSiteId: bookmark?.siteID,
        parentAssetId: bookmark?.assetID,
        deviceId: bookmark?.deviceID,
        siteName: bookmark?.siteName,
        assetName: bookmark?.assetName,
        deviceName: bookmark?.deviceName,
        parentDeviceTypeId: bookmark?.assetTypeId
      }
    })
  }

  const handleSiteOnClick = (): void => {
    if (!sidebarCollapsed) onCollapsedClick()
    setActiveItem('site')
    setActiveItemId('')
    navigate('/site-list')
  }

  const handleServiceOnClick = (): void => {
    if (!sidebarCollapsed) onCollapsedClick()
    setActiveItem('services')
    setActiveItemId('')
    navigate('/thermal-dashboard')
  }

  const handleHelpClick = (): any => {
    if (!sidebarCollapsed) onCollapsedClick()
    setActiveItem('help')
    if (userGuideUrl !== '') {
      window.open(userGuideUrl, '_blank')
    } else {
      // Load user guide file lazily when user clicks
      void getUserGuideFileUrl(
        (fileUrl, httpStatus) => {
          setUserGuideUrl(fileUrl)
          window.open(fileUrl, '_blank')
        },
        (errorMessage, httpStatus) => {
          // Error toast is shown by the service
        }
      )
    }
  }

  const handleLicenseClick = (e: React.MouseEvent): any => {
    if (licenseFileUrl === '') {
      e.preventDefault() // Prevent navigation until file is loaded

      // Load license file lazily when user clicks
      void getLicenseFileUrl(
        (fileUrl, httpStatus) => {
          setLicenseFileUrl(fileUrl)
          window.open(fileUrl, '_blank')
        },
        (errorMessage, httpStatus) => {
          // Error toast is shown by the service
        }
      )
    }
  }
  const handleDashboardClick = (): any => {
    if (!sidebarCollapsed) onCollapsedClick()
    setActiveItem('dashboard')
    setActiveItemId('')
  }
  const handleCompanyClick = (): any => {
    onCollapsedClick()
    setActiveItem('dashboard')
    setActiveItemId('company-menu')
  }
  const handleCollapsibleClick = (siteId: string, assetId: string): void => {
    onCollapsedClick()
    setActiveItem('dashboard')
    setActiveItemId(assetId)
  }

  const handleCollapsibleAssetClick = (
    siteId: string,
    assetId: string,
    siteName: string,
    assetName: string,
    assetType: string
  ): void => {
    setActiveItem('dashboard')
    setActiveItemId(assetId)
    navigate('/asset-dashboard', {
      state: {
        parentSiteId: siteId,
        parentAssetId: assetId,
        parentAssetTypeId: assetType,
        siteName,
        assetName
      }
    })
  }

  const handleDeviceClick = (
    siteId: string,
    assetId: string,
    deviceTypeId: string,
    deviceId: string,
    siteName: string,
    assetName: string,
    deviceName: string
  ): void => {
    setActiveItem('dashboard')
    setActiveItemId(deviceId)
    navigate('/device-dashboard', {
      state: {
        parentSiteId: siteId,
        parentAssetId: assetId,
        parentDeviceTypeId: deviceTypeId,
        deviceId,
        siteName,
        assetName,
        deviceName
      }
    })
  }

  const handleGetStartedOnClick = (): void => {
    setActiveItem('getStarted')
    navigate('/get-started')
  }

  const shouldShowSearchIcon = (): boolean => {
    return false
  }
  const handleMouseEnter = (): any => {
    setIsOpen(true)
  }
  const handleMouseLeave = (): any => {
    setIsOpen(false)
  }
  const formatTextWithTooltip = (text: string): JSX.Element => {
    if (text?.length > 12) {
      return (
        <Tooltip title={text}>
          <span>{`${text.substring(0, 12)}...`}</span>
        </Tooltip>
      )
    }
    return <span>{text !== '' ? text : 'NA'}</span>
  }

  const menuArr: any[] = (props?.menuInfo?.menuInfo ?? []).reduce(
    (acc: any[], curr: any) => {
      const existingCompany = acc.find(
        (item: any) => item.companyName === curr.companyName
      )
      if (existingCompany !== undefined) {
        existingCompany.sites.push(curr.site)
      } else {
        acc.push({ companyName: curr.companyName, sites: [curr.site] })
      }
      return acc
    },
    []
  )

  return (
    <div id='MainLayoutFlexContainer' data-testid='Home'>

      <Header
        title=''
        menuIcon=''
        onMenuToggle={() => {
          onCollapsedClick()
        }}
      >
      <div className="custom-menu-icon">
        <button onClick={onCollapsedClick}>
          {sidebarCollapsed ? <MenuIcon /> : <CloseIcon />}
        </button>
      </div>
        {shouldShowSearchIcon() && <Header.Search onSearch={() => undefined} />}
        {auth.isAuthenticated
          ? (
          <Header.UserProfile
            firstName={auth.user?.profile.given_name ?? ''}
            lastName={auth.user?.profile.family_name}
            imageUrl={auth.user?.profile.picture}
            onClick={handleMouseEnter}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-testid='user-profile'
          >
            {isOpen && (
              <>
                <Header.UserProfile.Item
                  data-testid='admin-btn'
                  onClick={() => {
                    setIsOpen(false)
                    window.open(
                      window._env_.REACT_APP_FORGE_IAM_ADMIN_CARD,
                      '_blank'
                    )
                  }}
                >
                  Admin
                </Header.UserProfile.Item>
                <Header.UserProfile.Item
                  data-testid='logout-id'
                  onClick={onLogout}
                >
                  Log out
                </Header.UserProfile.Item>
              </>
            )}
          </Header.UserProfile>
            )
          : (
          <></>
            )}
      </Header>
      <div id='bottom-flex-container'>
        <SidebarLayout collapsed={sidebarCollapsed} className='example-sidebar'>
          <SidebarLayout.Sidebar>
            <SidebarLayout.Sidebar.Item
              onClick={() => {
                if (defaultHomeScreen != null) {
                  handleHomeOnClick(defaultHomeScreen)
                }
              }}
              content={
                <span
                  data-testid="homeScreenMenu"
                  style={{
                    fontWeight: defaultHomeScreen !== null && defaultHomeScreen !== undefined ? 'normal' : 'bold',
                    color: defaultHomeScreen !== null && defaultHomeScreen !== undefined ? '#FFFFFF' : '#4B4B4B'
                  }}
                >
                  {defaultHomeScreen?.deviceID !== null &&
                  defaultHomeScreen?.deviceID !== undefined
                    ? defaultHomeScreen?.deviceName
                    : defaultHomeScreen?.assetID !== null &&
                      defaultHomeScreen?.assetID !== undefined
                      ? defaultHomeScreen?.assetName
                      : getBookmarkDataRaw?.isLoading ? 'Loading...' : 'Not Assigned'}
                </span>
              }
              icon={
                <img
                  src={defaultHomeScreen !== null && defaultHomeScreen !== undefined ? HomeIcon : HomeIconDisabled}
                  style={{ width: '16px', height: '16px' }}
                />
              }
              data-testid='home-icon'
            />
            <SidebarLayout.Sidebar.Item

              active={activeItem === 'bookmarks'}
              onClick={() => {
                const booksmarks = getBookmarkData?.filter((e) => e?.bookmarkType === 'BOOKMARK')
                if (booksmarks !== undefined && booksmarks?.length > 0) {
                  onCollapsedClick()
                }
              }}
              icon={
              <img
                src={(getBookmarkData !== null && getBookmarkData !== undefined && getBookmarkData?.filter((e) => e?.bookmarkType === 'BOOKMARK')?.length > 0) ? Bookmarks : Bookmark_disabled}
                style={{ width: '16px', height: '16px' }}
              />
              }
              key='bookmarks-menu'
              content={
                <span
                  style={{
                    fontWeight: getBookmarkData !== null && getBookmarkData !== undefined && getBookmarkData?.filter((e) => e?.bookmarkType === 'BOOKMARK')?.length > 0 ? 'normal' : 'bold',
                    color: getBookmarkData !== null && getBookmarkData !== undefined && getBookmarkData?.filter((e) => e?.bookmarkType === 'BOOKMARK')?.length > 0 ? '#FFFFFF' : '#4B4B4B'
                  }}
                >
                  Bookmarks
                </span>
              }
            />
            {!sidebarCollapsed &&
              getBookmarkData
                ?.filter((e) => e?.bookmarkType === 'BOOKMARK')
                ?.map((bookmark, index: number) => {
                  if (
                    bookmark?.deviceID !== null &&
                bookmark?.deviceID !== undefined
                  ) {
                    return (
                <SidebarLayout.Sidebar.Item
                  className='bookmark-menu'
                  key={`bookmark-device-${index}`}
                  data-testid={`bookmark-device-${index}`}
                  onClick={() => {
                    setHierarchyPaths([bookmark?.siteID, bookmark?.assetID, bookmark?.deviceID ?? ''])
                    setActiveItem('dashboard')
                    setActiveItemId(bookmark?.deviceID ?? bookmark?.assetID)
                    navigate('/device-dashboard', {
                      state: {
                        parentSiteId: bookmark?.siteID,
                        parentAssetId: bookmark?.assetID,
                        deviceId: bookmark?.deviceID,
                        siteName: bookmark?.siteName,
                        assetName: bookmark?.assetName,
                        deviceName: bookmark?.deviceName,
                        parentDeviceTypeId: bookmark?.assetTypeId
                      }
                    })
                  }}
                  icon={
                  bookmark?.dashboardId ===
                  defaultHomeScreen?.dashboardId
                    ? (
                    <img
                    src={HomeIcon}
                    style={{ width: '16px', height: '16px' }}
                    />
                      )
                    : (
                    <img
                              src={DeviceIcon}
                              style={{ width: '16px', height: '16px' }}
                            />
                      )
                  }
                  content={formatTextWithTooltip(
                    bookmark.deviceName ?? 'NA'
                  )}
                />
                    )
                  } else if (bookmark?.assetID != null) {
                    return (
                <SidebarLayout.Sidebar.Item
                  className='bookmark-menu'
                  key={`bookmark-asset-${index}`}
                  data-testid={`bookmark-asset-${index}`}
                  onClick={() => {
                    setActiveItemId(bookmark?.assetID ?? '')
                    navigate('/asset-dashboard', {
                      state: {
                        parentSiteId: bookmark?.siteID,
                        parentAssetId: bookmark?.assetID,
                        parentAssetTypeId: bookmark?.assetTypeId,
                        siteName: bookmark?.siteName,
                        assetName: bookmark?.assetName
                      }
                    })
                  }}
                  icon={
                  bookmark?.dashboardId ===
                  defaultHomeScreen?.dashboardId
                    ? (
                    <img
                    src={HomeIcon}
                    style={{ width: '16px', height: '16px' }}
                    />
                      )
                    : (
                    <img
                              src={AssetIcon}
                              style={{ width: '16px', height: '16px' }}
                            />
                      )
                  }
                  content={formatTextWithTooltip(
                    bookmark.assetName ?? 'NA'
                  )}
                />
                    )
                  }
                  return null
                })}
            {menuArr?.length > 0
              ? (
                  menuArr.map((company: any, companyIndex: number) =>
                    Array.isArray(company.sites) && company.sites.length > 0
                      // eslint-disable-next-line multiline-ternary
                      ? (
                  <CollapsibleSidebarItem
                    isSelected={false}
                    isParent={true}
                    expand={hierarchyPaths.length > 0}
                    // isSelected={activeItemId === 'company-menu'}
                    onClick={() => {
                      handleCompanyClick()
                    }}
                    icon={
                      <img
                        src={TenantIcon}
                        style={{ width: '16px', height: '16px' }}
                      />
                    }
                    key={`company-${companyIndex}`}
                    isActive={false}
                    content={company.companyName}
                  >
                    {company.sites.map((site: any) =>
                      Array.isArray(site?.assetList) &&
                      site?.assetList.length > 0
                        ? (
                        <CollapsibleSidebarItem
                          isSelected={activeItemId === site.id}
                          expand={site?.assetList
                            ?.map((e: any) => {
                              return e.id
                            })
                            .filter((val: any) => hierarchyPaths.includes(val))?.length > 0}
                          onClick={() => {
                            handleCollapsibleClick('site', site.id)
                          }}
                          icon={
                            <img
                              src={SiteIcon}
                              style={{ width: '16px', height: '16px' }}
                            />
                          }
                          key={`site-${site?.id as string}`}
                          isActive={false}
                          content={site.name}
                        >
                          {site?.assetList?.map((asset: Asset) => {
                            const devices =
                              asset.gateway != null &&
                              Array.isArray(asset.gateway) &&
                              asset.gateway.length > 0
                                ? asset.gateway?.flatMap(
                                  (gateway: Gateway) => gateway.device ?? []
                                )
                                : []
                            return devices.length > 0
                              ? (
                              <CollapsibleSidebarItem
                                isSelected={activeItemId === asset.id}
                                expand={devices
                                  ?.map((e: any) => {
                                    return e.id
                                  })
                                  .filter((val: any) => hierarchyPaths.includes(val))?.length > 0}
                                onClick={() => {
                                  handleCollapsibleAssetClick(
                                    site.id,
                                    asset.id,
                                    site.name,
                                    asset.name,
                                    asset.assetType
                                  )
                                }}
                                icon={
                                  <img
                                    src={AssetIcon}
                                    style={{ width: '16px', height: '16px' }}
                                  />
                                }
                                key={`asset-${asset.id}`}
                                isActive={false}
                                content={asset.name}
                              >
                                {devices.map((device: Device) => (
                                  <SidebarLayout.Sidebar.Item
                                    onClick={() => {
                                      handleDeviceClick(
                                        site.id,
                                        asset.id,
                                        device.deviceTypeId,
                                        device.id,
                                        site.name,
                                        asset.name,
                                        device.name
                                      )
                                    }}
                                    icon={
                                      <img
                                        src={DeviceIcon}
                                        style={{
                                          width: '16px',
                                          height: '16px'
                                        }}
                                      />
                                    }
                                    key={`device-${device.id}`}
                                    content={
                                      <span
                                        className={
                                          activeItemId === device.id
                                            ? 'menu-item-select-color'
                                            : ''
                                        }
                                      >
                                        {formatTextWithTooltip(device.name)}
                                      </span>
                                    }
                                  />
                                ))}
                              </CollapsibleSidebarItem>
                                )
                              : (
                              <SidebarLayout.Sidebar.Item
                                onClick={() => {
                                  handleCollapsibleAssetClick(
                                    site.id,
                                    asset.id,
                                    site.name,
                                    asset.name,
                                    asset.assetType
                                  )
                                }}
                                icon={
                                  <img
                                    src={AssetIcon}
                                    style={{ width: '16px', height: '16px' }}
                                  />
                                }
                                key={`asset-${asset.id}`}
                                content={
                                  <span
                                    className={
                                      activeItemId === asset.id
                                        ? 'menu-item-select-color'
                                        : ''
                                    }
                                  >
                                    {formatTextWithTooltip(asset.name)}
                                  </span>
                                }
                              />
                                )
                          })}
                        </CollapsibleSidebarItem>
                          )
                        : (
                        <SidebarLayout.Sidebar.Item
                          onClick={() => {
                            handleCollapsibleClick('site', site.id)
                          }}
                          icon={
                            <img
                              src={SiteIcon}
                              style={{ width: '16px', height: '16px' }}
                            />
                          }
                          key={`site-${site.id as string}`}
                          content={
                            <span
                              className={
                                activeItemId === site.id
                                  ? 'menu-item-select-color'
                                  : ''
                              }
                            >
                              {formatTextWithTooltip(site.name)}
                            </span>
                          }
                        />
                          )
                    )}
                  </CollapsibleSidebarItem>
                        ) : (
                  <SidebarLayout.Sidebar.Item
                    key={`company-${companyIndex}`}
                    content={formatTextWithTooltip(company.companyName)}
                    icon=''
                  />
                        )
                  )
                )
              : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <SidebarLayout.Sidebar.Item
                  onClick={handleDashboardClick}
                  active={activeItem === 'dashboard'}
                  content='Loading...'
                  icon={
                    <img
                      src={TenantIcon}
                      style={{ width: '16px', height: '16px' }}
                    />
                  }
                  data-testid='dashboard-icon'
                />
                {props?.menuInfo?.isLoading && (
                  <div
                    className='dashboard-loading'
                    style={{ marginLeft: 'auto', marginRight: '16px' }}
                  >
                    <CircularProgress size={20} />
                  </div>
                )}
              </div>
                )}
            <SidebarLayout.Sidebar.Item
              onClick={handleServiceOnClick}
              active={activeItem === 'services'}
              content='App Setting'
              icon={
                <img src={KpiIcon} style={{ width: '16px', height: '16px' }} />
              }
              data-testid='kpi-icon'
            />
            <SidebarLayout.Sidebar.Item
              onClick={handleSiteOnClick}
              active={activeItem === 'site'}
              content='Site Configuration'
              icon={
                <img
                  src={SettingIcon}
                  style={{ width: '16px', height: '16px' }}
                />
              }
              data-testid='setting-icon'
            />
            <SidebarLayout.Sidebar.Item
              onClick={handleGetStartedOnClick}
              active={activeItem === 'getStarted'}
              content='Get Started'
              icon={
                <img
                  src={GetStarted}
                  style={{ width: '16px', height: '16px' }}
                />
              }
              data-testid='Get-icon'
            />
            <SidebarLayout.Sidebar.Item
              onClick={handleHelpClick}
              active={activeItem === 'help'}
              content='Help'
              icon={
                <img src={HelpIcon} style={{ width: '16px', height: '16px' }} />
              }
              data-testid='help-icon'
            />
          </SidebarLayout.Sidebar>
          <SidebarLayout.Content>
            <div
              id='sidebar-layout-content-flex-container'
              className='sidebar-layout-content'
            >
              <div className='outlet-content'>
                <Outlet context={{ sidebarCollapsed }} />
              </div>
              <Footer>
                <Footer.Item>
                  {dashboardStrings.Version} - {serviceVersion}
                </Footer.Item>
                <Footer.Item
                  href='https://www.honeywell.com/us/en/privacy-statement'
                  target='_blank'
                >
                  {dashboardStrings.Privacy_Policy}
                </Footer.Item>
                <Footer.Item href='https://Honpat.com' target='_blank'>
                  {dashboardStrings.Patents}
                </Footer.Item>
                <Footer.Item
                  href={licenseFileUrl !== '' ? licenseFileUrl : '#'}
                  target='_blank'
                  onClick={handleLicenseClick}
                >
                  {dashboardStrings.License}
                </Footer.Item>
              </Footer>
            </div>
          </SidebarLayout.Content>
        </SidebarLayout>
      </div>
      <div className='IdleModal'>
        {auth.isAuthenticated ? <IdleModal onLogout={onLogout} /> : <></>}
      </div>
    </div>
  )
}

interface DispatchToProps {
  fetchSidebarMenuInfo: () => void
  getBookmarkListDataService: (subId: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchSidebarMenuInfo: () => dispatch(fetchSidebarMenuInfo()),
  getBookmarkListDataService: (subId: string) =>
    dispatch(getBookmarkListDataservice(subId))
})
interface StateToProps {
  menuInfo: MenuInfoState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  menuInfo: state.menuInfoState
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

type AllProps = PropsFromRedux

export default connector(DashboardLayout)
